import type { InferOutput } from 'valibot'
import { number, object, optional, parse, record, string, unknown } from 'valibot'
import { isString } from '~/utils/string'

const lastActivitySchema = object({
  routeName: string(),
  title: string(),
  timestamp: number(),
  params: optional(record(string(), unknown())),
})

type LastActivity = InferOutput<typeof lastActivitySchema>

export function useLastActivity() {
  const { loggedIn: isLastActivityEnabled } = useUserSession()
  const personalization = usePersonalization('last-activity')
  const router = useRouter()

  const VALID_ROUTE_NAMES = router.getRoutes().map(r => r.name).filter(isString)

  const lastActivity = computed<LastActivity | null>(() => {
    const data = personalization.data.value

    if (!data) {
      return null
    }

    try {
      return parse(lastActivitySchema, data)
    }
    catch (error) {
      console.warn('Failed to parse last activity data, clearing:', error)
      personalization.del()
      return null
    }
  })

  const initialized = computed(() => personalization.isSuccess.value)
  const isLoading = computed(() => personalization.isLoading.value)

  function setLastActivity(routeName: string, title: string, params?: Record<string, unknown>) {
    if (!isLastActivityEnabled.value) {
      return
    }

    if (!VALID_ROUTE_NAMES.includes(routeName)) {
      return
    }

    const activity: LastActivity = {
      routeName,
      title,
      timestamp: Date.now(),
      ...(params && { params }),
    }

    personalization.set(activity)
  }

  function clearLastActivity() {
    if (!isLastActivityEnabled.value) {
      return
    }

    personalization.del()
  }

  return {
    lastActivity,
    isLastActivityEnabled,
    initialized,
    isLoading,
    setLastActivity,
    clearLastActivity,
  }
}

/**
 * Opt-in composable for pages to track last activity
 * Call this in pages where you want to track last activity
 */
export function useTrackLastActivity(title: MaybeRefOrGetter<string | undefined | null>, params?: MaybeRefOrGetter<Record<string, unknown> | undefined>) {
  const route = useRoute()
  const { isLastActivityEnabled, setLastActivity } = useLastActivity()

  if (!isLastActivityEnabled.value || !route.name) {
    return
  }

  watch(() => toValue(title), (title) => {
    if (title && route.name) {
      setLastActivity(String(route.name), title, toValue(params) ?? route.params)
    }
  }, {
    immediate: true,
  })
}
